import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Star, Lock, BrainCircuit, Play, Loader2, AlertCircle, BookOpen, ClipboardCheck, Zap, GitCompareArrows, Sigma } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import TopBar from "../components/TopBar";
import { ScoreChart } from "../components/ScoreChart";

const DIFF_COLOR = { base: "#00E5FF", intermedio: "#6C63FF", esame: "#FF3366" };

export default function ExamDetail() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const tr = useT();
  const [exam, setExam] = useState(null);
  const [history, setHistory] = useState([]);

  const load = () => api.get(`/exams/${examId}`).then((r) => { setExam(r.data.exam); if (r.data.user) setUser(r.data.user); }).catch(() => {});
  useEffect(() => {
    load();
    api.get(`/exams/${examId}/simulations`).then((r) => setHistory(r.data.history)).catch(() => {});
    /* eslint-disable-next-line */
  }, [examId]);

  // poll while generating
  useEffect(() => {
    if (exam?.status === "generating") {
      const t = setTimeout(load, 5000);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [exam]);

  if (!exam) return <div className="space-bg min-h-screen flex items-center justify-center text-white/60">{tr("Caricamento...")}</div>;

  const generating = exam.status === "generating";
  const prog = exam.progress || {};

  return (
    <div className="space-bg min-h-screen pb-20">
      <TopBar />
      <div className="max-w-lg mx-auto px-4 pt-5">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-white/60 bouncy mb-4" data-testid="examdetail-back-btn">
          <ChevronLeft size={18} /> {tr("Home")}
        </button>
        <h1 className="font-heading font-black text-3xl tracking-tight mb-1">{exam.name}</h1>
        {exam.book_ref && <p className="text-white/40 text-xs mb-4">📖 {exam.book_ref}</p>}

        {exam.status === "ready" && exam.topics.length > 0 && (
          <button onClick={() => navigate(`/simulation/${examId}`)}
            className="w-full glass rounded-2xl p-4 mb-6 flex items-center gap-3 bouncy relative overflow-hidden" data-testid="simulation-btn">
            <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-20" style={{ background: "var(--pink)" }} />
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "rgba(255,0,127,0.15)", boxShadow: "0 0 16px rgba(255,0,127,0.4)" }}>
              <ClipboardCheck size={24} style={{ color: "var(--pink)" }} />
            </div>
            <div className="flex-1 text-left">
              <h3 className="font-heading font-bold tracking-tight">{tr("Simulazione d'esame")}</h3>
              <p className="text-xs text-white/60">{tr("Cronometrata, domande da tutti gli argomenti, esercizi nuovi ogni volta")}</p>
            </div>
          </button>
        )}

        {exam.status === "ready" && exam.topics.length > 0 && (
          <button onClick={() => navigate(`/quick/${examId}`)}
            className="w-full glass rounded-2xl p-4 mb-6 flex items-center gap-3 bouncy" data-testid="quick-review-btn">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "rgba(0,229,255,0.15)", boxShadow: "0 0 14px rgba(0,229,255,0.35)" }}>
              <Zap size={22} style={{ color: "var(--cyan)" }} />
            </div>
            <div className="flex-1 text-left">
              <h3 className="font-heading font-bold tracking-tight">{tr("Ripasso veloce")}</h3>
              <p className="text-xs text-white/60">{tr("Domande rapide a crocette da tutti gli argomenti")}</p>
            </div>
          </button>
        )}

        {history.length > 0 && (
          <div className="glass rounded-2xl p-4 mb-6" data-testid="sim-history">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-bold text-sm tracking-tight">{tr("📈 Storico simulazioni")}</h3>
              {history.length >= 2 && (
                <button onClick={() => navigate(`/compare/${examId}`)} className="text-xs bouncy flex items-center gap-1" style={{ color: "var(--cyan)" }} data-testid="compare-btn">
                  <GitCompareArrows size={14} /> {tr("Confronta")}
                </button>
              )}
            </div>
            <p className="text-xs text-white/50 mb-3">{tr("Ultimo voto:")} <b style={{ color: "var(--cyan)" }}>{history[history.length - 1].voto}/30</b> · {history.length} {tr("tentativi")}</p>
            <ScoreChart points={history.map((h, i) => ({ label: `#${i + 1}`, value: h.overall }))} />
          </div>
        )}

        {generating && (
          <div className="glass rounded-3xl p-6 mb-6 text-center" data-testid="exam-generating">
            <Loader2 size={36} className="animate-spin mx-auto mb-3" style={{ color: "var(--cyan)" }} />
            <p className="font-heading font-bold">{tr("L'AI sta preparando il tuo esame...")}</p>
            <p className="text-white/60 text-sm mt-1">
              {prog.total ? `${tr("argomenti")} ${prog.done}/${prog.total}` : tr("Sto analizzando e dividendo gli appunti")}
            </p>
            {prog.total > 0 && (
              <div className="mt-3 h-2 rounded-full bg-black/40 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${(prog.done / prog.total) * 100}%`, background: "var(--cyan)", boxShadow: "0 0 10px var(--cyan)" }} />
              </div>
            )}
          </div>
        )}

        {exam.status === "error" && (
          <div className="glass rounded-3xl p-6 mb-6 text-center" data-testid="exam-error">
            <AlertCircle size={32} style={{ color: "var(--danger)" }} className="mx-auto mb-2" />
            <p className="text-white/70">{tr("Errore nella generazione. Elimina l'esame e riprova con appunti più chiari.")}</p>
          </div>
        )}

        {exam.topics.length === 0 && !generating && exam.status !== "error" && (
          <p className="text-center text-white/50 mt-6">{tr("Nessun argomento.")}</p>
        )}

        <div className="space-y-5">
          {exam.topics.map((t) => (
            <motion.div key={t.topic_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-5" data-testid={`topic-card-${t.topic_id}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading font-bold text-lg tracking-tight">{t.name}</h3>
                <span className="text-xs text-white/40 font-num shrink-0 ml-2">{t.total_questions || 0} {tr("domande")}</span>
              </div>
              {t.micro_topics?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {t.micro_topics.map((m, mi) => (
                    <span key={mi} className="text-xs px-2.5 py-1 rounded-full bg-white/10 text-white/70">{m}</span>
                  ))}
                </div>
              )}
              <div className="space-y-2 mb-4">
                {t.levels.map((lv) => {
                  const c = DIFF_COLOR[lv.difficulty] || "#00E5FF";
                  return (
                    <button key={lv.level_id} disabled={!lv.unlocked} onClick={() => navigate(`/quiz/${lv.level_id}`)}
                      className="w-full flex items-center gap-3 bg-black/30 rounded-2xl px-4 py-3 bouncy disabled:opacity-50" data-testid={`exam-level-${lv.level_id}`}>
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${c}22`, boxShadow: lv.unlocked ? `0 0 12px ${c}55` : "none" }}>
                        {lv.unlocked ? <Play size={16} style={{ color: c }} /> : <Lock size={16} className="text-white/40" />}
                      </div>
                      <div className="flex-1 text-left">
                        <span className="font-heading font-bold text-sm">{lv.title}</span>
                        <span className="text-xs text-white/40 block font-num">{lv.num_questions} {tr("domande")}</span>
                      </div>
                      <div className="flex gap-0.5">
                        {[1, 2, 3].map((s) => <Star key={s} size={13} className={s <= lv.stars ? "fill-current" : ""} style={{ color: s <= lv.stars ? "var(--gold)" : "rgba(255,255,255,0.2)" }} />)}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => navigate(`/theory/${examId}/${t.topic_id}`)}
                  className="py-2.5 rounded-2xl btn-neon font-heading font-bold text-sm bouncy flex items-center justify-center gap-2" data-testid={`theory-btn-${t.topic_id}`}>
                  <BookOpen size={16} /> {tr("Teoria")}
                </button>
                <button onClick={() => navigate(`/formulas/${examId}/${t.topic_id}`)}
                  className="py-2.5 rounded-2xl font-heading font-bold text-sm bouncy flex items-center justify-center gap-2" style={{ background: "rgba(255,193,7,0.15)", color: "var(--gold)", border: "1px solid rgba(255,193,7,0.4)" }} data-testid={`formulas-btn-${t.topic_id}`}>
                  <Sigma size={16} /> {tr("Formulario")}
                </button>
                {t.card_count > 0 && (
                  <button onClick={() => navigate(`/memory/${examId}/${t.topic_id}`)}
                    className="col-span-2 py-2.5 rounded-2xl btn-violet font-heading font-bold text-sm bouncy flex items-center justify-center gap-2" data-testid={`memory-btn-${t.topic_id}`}>
                    <BrainCircuit size={16} /> {tr("Memorizza")} ({t.mastered_cards || 0}/{t.card_count})
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
